import type { ReactNode } from "react";
import type { SlaState } from "../lib/types";

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`card p-6 ${className}`}>{children}</div>;
}

export function SectionTitle({
  kicker,
  title,
  desc,
}: {
  kicker?: string;
  title: string;
  desc?: string;
}) {
  return (
    <div className="mb-6">
      {kicker && (
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-accent">{kicker}</div>
      )}
      <h1 className="mt-1 text-2xl font-semibold tracking-tight text-ink">{title}</h1>
      {desc && <p className="mt-2 max-w-2xl text-sm text-ink-muted">{desc}</p>}
    </div>
  );
}

export function Banner({
  tone = "info",
  children,
}: {
  tone?: "info" | "success" | "error" | "warning";
  children: ReactNode;
}) {
  const cls =
    tone === "error"
      ? "border-danger/40 bg-danger/10 text-danger"
      : tone === "success"
        ? "border-accent/40 bg-accent/10 text-accent-strong"
        : tone === "warning"
          ? "border-amber-400/40 bg-amber-400/10 text-amber-300"
          : "border-line bg-bg-soft text-ink-muted";
  return <div className={`rounded-sm border px-4 py-3 text-sm ${cls}`}>{children}</div>;
}

export function SlaBadge({ state }: { state?: SlaState }) {
  if (!state) return <span className="badge">SLA n/a</span>;
  const label =
    state === "at_risk"
      ? "At risk"
      : state === "breached"
        ? "Breached"
        : state === "met"
          ? "Met"
          : state === "paused"
            ? "Paused"
            : "On track";
  const cls =
    state === "breached"
      ? "badge text-danger"
      : state === "at_risk"
        ? "badge text-amber-500"
        : state === "met"
          ? "badge-accent"
          : "badge";
  return <span className={cls}>{label}</span>;
}

export function ageLabel(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "just now";
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

export function Stat({
  label,
  value,
  tone = "ok",
}: {
  label: string;
  value: number | string;
  tone?: "ok" | "warn";
}) {
  return (
    <Card>
      <div className={`text-3xl font-semibold ${tone === "warn" ? "text-danger" : "text-ink"}`}>
        {value}
      </div>
      <div className="mt-1 text-xs uppercase tracking-wider text-ink-dim">{label}</div>
    </Card>
  );
}

/**
 * Render a ticket description/comment body. When the server supplied sanitized
 * Markdown HTML (`rendered`), inject it (already escaped + whitelisted server-side, §4);
 * otherwise fall back to the raw text with preserved whitespace.
 */
export function MarkdownBody({
  rendered,
  raw,
  className = "",
}: {
  rendered?: string;
  raw: string;
  className?: string;
}) {
  if (rendered && rendered.trim() !== "") {
    return (
      <div
        className={`ticket-md text-sm text-ink ${className}`}
        dangerouslySetInnerHTML={{ __html: rendered }}
      />
    );
  }
  return <div className={`whitespace-pre-wrap text-sm text-ink ${className}`}>{raw}</div>;
}

/**
 * A left-to-right sliding on/off switch. Off = knob on the left (grey track);
 * on = knob slid right on a light-green track. Disabled while `busy` (mid-request).
 */
export function Toggle({
  checked,
  busy = false,
  onChange,
  title,
}: {
  checked: boolean;
  busy?: boolean;
  onChange: (next: boolean) => void;
  title?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      title={title}
      disabled={busy}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 flex-none items-center rounded-full transition-colors disabled:opacity-60 ${
        checked ? "bg-accent" : "bg-line"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(1)} ${units[i]}`;
}
